// Moving a module by its header (05e-2) — main.js's header drag, ported as a hook on
// PlacedModule's header. One module, or the whole selection when it's part of one (Ben's
// #173 group move); a press that doesn't travel 4px is a click that toggles the selection
// (#178). ONE undo step per move, taken at the press; a click takes it back off.
//
// During the drag the page is the truth (05e's fork 2A): the element's style is written
// every frame, and the canvas is held open (every module moved by DRAG_EXPANSION) — the
// store gets the boxes once the canvas closes again (commitBoxes in collapseWorkspace).
//
// Dropping onto a module it combines with: what happens is drop-actions.ts — merge (05g-2),
// switch or Similarity's overlay (05g-1).

import { useCallback, type PointerEvent as ReactPointerEvent, type RefObject } from "react";
import { GRID_GAP } from "./module-shell.js";
import { STEP } from "./placement.js";
import {
  collapseWorkspace,
  collapseWorkspaceOnTarget,
  expandWorkspace,
  findOverlappingModule,
  getSnapCoords,
  isAreaClear,
  moduleFootprint,
} from "./canvas-space.js";
import { refreshResizeDominance } from "./resize-dominance.js";
import { joinTeam, mergeTeamModules, teamJoinsTile, teamModuleDrop, teamRefusedBy } from "./team-merge.js";
import {
  absorb,
  groupJoinersOnto,
  joinGroupPlayers,
  mergeModules,
  resolveModuleDropAction,
  switchModulePlayer,
  switchToSimilarityOverlay,
  type GroupJoin,
} from "./drop-actions.js";
import { matchModuleDrop, showMatchFromModule } from "./match-drag.js";
import { applyTeamDrop, heldTeamDrop, heldTeamOf } from "./team-modules.js";
import { panelGoesBack, reattachPanel } from "./map-panels.js";
import { canvasApi } from "./shell-actions.js";
import type { ModuleDropAction } from "./undo.js";
import { deselect, isSelected, selectedModules, toggleSelected } from "./selection.js";
import { undoApi } from "./undo.js";
import { getScale as scale } from "./zoom.js";
import {
  autoScrollSpeed,
  followerLanding,
  followerOffset,
  isDragStart,
  moveDrop,
  type FollowerOffset,
} from "./move-rules.js";
import { hideTooltip } from "./tooltip.js";

interface Follower extends FollowerOffset {
  el: HTMLElement;
  /** Where it started — an invalid drop puts it back. */
  startLeft: number;
  startTop: number;
}

/** What dropping `mod` on `target` does: a Match's game onto a map or a Match, a Squad's team onto
 *  a module that shows teams, else the drop rules. */
function dropActionOf(mod: HTMLElement, target: HTMLElement): ModuleDropAction | null {
  if (panelGoesBack(mod, target)) return "attach-panel";
  const matchOrTeam = matchModuleDrop(mod, target) ?? heldTeamDrop(mod, target);
  // A team already on the target's comparison, or a full one: red, as a dragged team name.
  const held = matchOrTeam === "show-team" ? heldTeamOf(mod) : null;
  if (held && teamRefusedBy(target, held)) return null;
  if (matchOrTeam) return matchOrTeam;
  // Teams on Stats tiles and Radars combine as teams, never as the players underneath (team-merge.ts).
  const teams = teamModuleDrop(mod, target);
  return teams !== undefined ? teams : resolveModuleDropAction(mod, target);
}

/** The one drag in progress (main.js kept these as file-level variables). */
interface Drag {
  mod: HTMLElement;
  /** The press point in page coordinates. */
  startX: number;
  startY: number;
  /** The module's box at the press (already moved out by the canvas opening). */
  initialLeft: number;
  initialTop: number;
  lastClientX: number;
  lastClientY: number;
  speedX: number;
  speedY: number;
  followers: Follower[];
  /** One extra outline per follower, so every member shows where it will land. */
  groupIndicators: HTMLElement[];
  /** Past the click threshold — a real move now. */
  pressMoved: boolean;
  /** The selection a drag of an UNselected module drops — held until the press really
   *  becomes a drag, so a click can add to it instead. */
  pendingSelectionClear: HTMLElement[] | null;
  /** The "lifted" look (dimmed module(s), outlines), also held until then, so a click
   *  doesn't flicker. */
  pendingVisuals: (() => void) | null;
}

let drag: Drag | null = null;
// The auto-scroll loop runs while a drag is on; never two at once (a quick second press
// could otherwise start another before the first sees its drag end — double speed).
let autoScrollRunning = false;

function snapIndicator(): HTMLElement {
  const el = document.getElementById("snap-indicator");
  if (!el) throw new Error("#snap-indicator missing from legacy-scaffold.html");
  return el;
}

const px = (v: string) => Number.parseInt(v, 10);
const leftOf = (m: HTMLElement) => px(m.style.left || "0");
const topOf = (m: HTMLElement) => px(m.style.top || "0");

/**
 * A group's players joining a Radar / Stats tile (Ben, 2026-11-02): the module the HELD one
 * lands on (never a member of the group) and who of the group joins it, the held module's
 * player first, then the others top to bottom, left to right (a stable order, whatever order
 * they were selected in). No target or nobody joins → null, and it's a plain group move.
 */
function groupJoinOf(d: Drag, col: number, row: number, cols: number, rows: number): { target: HTMLElement; joins: GroupJoin[] } | null {
  const others = [...d.followers].sort((a, b) => a.startTop - b.startTop || a.startLeft - b.startLeft).map((f) => f.el);
  const group = new Set([d.mod, ...others]);
  const target = findOverlappingModule(col, row, cols, rows, group);
  if (!target) return null;
  const joins = groupJoinersOnto(target, [d.mod, ...others]);
  return joins.length ? { target, joins } : null;
}

/** One frame of the drag (main.js updateModuleDrag) — from a pointer move or an
 *  auto-scroll tick. */
function updateModuleDrag(d: Drag, newX: number, newY: number): void {
  const { mod } = d;
  mod.style.left = `${newX}px`;
  mod.style.top = `${newY}px`;
  // Followers keep their fixed pixel offset.
  for (const f of d.followers) {
    f.el.style.left = `${newX + f.offsetX}px`;
    f.el.style.top = `${newY + f.offsetY}px`;
  }

  const width = px(mod.style.width);
  const height = px(mod.style.height);
  const snap = getSnapCoords(newX, newY);
  const ind = snapIndicator();
  ind.style.display = "block";
  ind.style.width = `${width}px`;
  ind.style.height = `${height}px`;
  ind.style.left = `${snap.x}px`;
  ind.style.top = `${snap.y}px`;

  const cols = Math.round((width + GRID_GAP) / STEP);
  const rows = Math.round((height + GRID_GAP) / STEP);

  if (d.followers.length) {
    // The held module over a Radar / Stats tile some of the group's players join: blue on the
    // target, as a single merge; the others' outlines stay where they are (they don't move).
    const join = groupJoinOf(d, snap.col, snap.row, cols, rows);
    if (join) {
      const t = join.target;
      ind.classList.remove("invalid");
      ind.classList.add("merge");
      ind.style.width = `${px(t.style.width) || width}px`;
      ind.style.height = `${px(t.style.height) || height}px`;
      ind.style.left = `${px(t.style.left) || snap.x}px`;
      ind.style.top = `${px(t.style.top) || snap.y}px`;
      d.followers.forEach((f, i) => {
        const gi = d.groupIndicators[i];
        if (!gi) throw new Error("move: a follower has no outline");
        gi.style.left = `${f.startLeft}px`;
        gi.style.top = `${f.startTop}px`;
        gi.classList.remove("invalid", "merge");
      });
      return;
    }
    // Valid only if every member's landing cell is clear, ignoring the group's own
    // members; each keeps its own Notes exemption. Otherwise groups never merge or switch.
    const group = new Set([mod, ...d.followers.map((f) => f.el)]);
    let valid = isAreaClear(snap.col, snap.row, cols, rows, group, mod.classList.contains("module-notes"));
    d.followers.forEach((f, i) => {
      const fp = moduleFootprint(f.el);
      const col = snap.col + f.offsetCols;
      const row = snap.row + f.offsetRows;
      const gi = d.groupIndicators[i];
      if (!gi) throw new Error("move: a follower has no outline");
      gi.style.left = `${col * STEP}px`;
      gi.style.top = `${row * STEP}px`;
      gi.style.width = f.el.style.width;
      gi.style.height = f.el.style.height;
      if (!isAreaClear(col, row, fp.cols, fp.rows, group, f.el.classList.contains("module-notes"))) valid = false;
    });
    for (const el of [ind, ...d.groupIndicators]) {
      el.classList.remove("merge");
      el.classList.toggle("invalid", !valid);
    }
    return;
  }

  if (isAreaClear(snap.col, snap.row, cols, rows, mod)) {
    ind.classList.remove("invalid", "merge");
    return;
  }
  // In the way of something: normally red — but a module it combines with is a merge
  // waiting to happen (blue), and the outline takes the TARGET's box, so a small module
  // dragged onto a big one doesn't hide the glow under itself.
  const overlapping = findOverlappingModule(snap.col, snap.row, cols, rows, mod);
  if (overlapping && dropActionOf(mod, overlapping)) {
    ind.classList.remove("invalid");
    ind.classList.add("merge");
    // main.js's `|| fallback`: a target without an inline box keeps the dragged one's.
    ind.style.width = `${px(overlapping.style.width) || width}px`;
    ind.style.height = `${px(overlapping.style.height) || height}px`;
    ind.style.left = `${px(overlapping.style.left) || snap.x}px`;
    ind.style.top = `${px(overlapping.style.top) || snap.y}px`;
  } else {
    ind.classList.remove("merge");
    ind.classList.add("invalid");
  }
}

/** Scroll while the pointer sits near a screen edge, moving the module with the page
 *  (main.js desktopScrollTick — its own copy stays for the side-panel drag until 05h). */
function autoScrollTick(): void {
  const d = drag;
  if (!d) {
    autoScrollRunning = false;
    return;
  }
  if (d.speedX !== 0 || d.speedY !== 0) {
    window.scrollBy(d.speedX, d.speedY);
    const s = scale();
    updateModuleDrag(
      d,
      d.initialLeft + (d.lastClientX + window.scrollX - d.startX) / s,
      d.initialTop + (d.lastClientY + window.scrollY - d.startY) / s,
    );
  }
  requestAnimationFrame(autoScrollTick);
}

function onPointerMove(e: PointerEvent): void {
  const d = drag;
  if (!d) return;
  const movingLeft = e.clientX < d.lastClientX;
  const movingRight = e.clientX > d.lastClientX;
  const movingUp = e.clientY < d.lastClientY;
  const movingDown = e.clientY > d.lastClientY;
  d.lastClientX = e.clientX;
  d.lastClientY = e.clientY;
  d.speedX = autoScrollSpeed(e.clientX, window.innerWidth, movingLeft, movingRight);
  d.speedY = autoScrollSpeed(e.clientY, window.innerHeight, movingUp, movingDown);

  if (!d.pressMoved && isDragStart(e.pageX - d.startX, e.pageY - d.startY)) {
    d.pressMoved = true;
    if (d.pendingSelectionClear) deselect(d.pendingSelectionClear);
    d.pendingSelectionClear = null;
    d.pendingVisuals?.();
    d.pendingVisuals = null;
  }
  // Not a drag (yet) — the module stays exactly where it is.
  if (!d.pressMoved) return;
  const s = scale();
  updateModuleDrag(d, d.initialLeft + (e.pageX - d.startX) / s, d.initialTop + (e.pageY - d.startY) / s);
}

function judder(el: HTMLElement): void {
  el.classList.add("snap-judder");
  el.addEventListener("animationend", () => el.classList.remove("snap-judder"), { once: true });
}

function unlift(el: HTMLElement): void {
  el.style.zIndex = "";
  el.style.opacity = "";
}

function onPointerUp(): void {
  const d = drag;
  if (!d) return;
  drag = null; // stops the auto-scroll loop too
  document.removeEventListener("pointermove", onPointerMove);
  document.removeEventListener("pointerup", onPointerUp);
  document.body.classList.remove("is-moving-module");

  const { mod } = d;
  const ind = snapIndicator();
  const wasHeaderClick = !d.pressMoved;
  const dropSnap = getSnapCoords(px(mod.style.left), px(mod.style.top));
  const dropCols = Math.round((px(mod.style.width) + GRID_GAP) / STEP);
  const dropRows = Math.round((px(mod.style.height) + GRID_GAP) / STEP);
  const isGroup = d.followers.length > 0;
  // Groups never merge or switch — only a single module can — except that a group's players
  // join a Radar / Stats tile the held module lands on (groupJoinOf).
  const overlapping =
    isGroup || wasHeaderClick ? null : findOverlappingModule(dropSnap.col, dropSnap.row, dropCols, dropRows, mod);
  const groupJoin = isGroup && !wasHeaderClick ? groupJoinOf(d, dropSnap.col, dropSnap.row, dropCols, dropRows) : null;
  const drop = moveDrop({
    wasHeaderClick,
    isGroup,
    indicatorInvalid: ind.classList.contains("invalid"),
    dropAction: overlapping ? dropActionOf(mod, overlapping) : null,
    groupJoins: groupJoin !== null,
  });
  const { withCheckpointHeld, dropLastCheckpoint } = undoApi();

  switch (drop.kind) {
    case "group": {
      const finalLeft = drop.invalid ? d.initialLeft : px(ind.style.left);
      const finalTop = drop.invalid ? d.initialTop : px(ind.style.top);
      mod.style.left = `${finalLeft}px`;
      mod.style.top = `${finalTop}px`;
      unlift(mod);
      // BEFORE collapseWorkspace: it moves every module back out of the opened canvas, so
      // a follower placed after it would be stranded in the wrong coordinates.
      for (const f of d.followers) {
        const at = drop.invalid ? { left: f.startLeft, top: f.startTop } : followerLanding(finalLeft, finalTop, f);
        f.el.style.left = `${at.left}px`;
        f.el.style.top = `${at.top}px`;
        unlift(f.el);
      }
      collapseWorkspace(finalLeft, finalTop, drop.invalid);
      if (!drop.invalid) [mod, ...d.followers.map((f) => f.el)].forEach(judder);
      break;
    }
    case "group-join": {
      // Everyone goes back where they were (the modules stay: Ben, 2026-11-02), then their
      // players join the target in one go. The press already checkpointed: one Undo step
      // brings back the target and every position.
      if (!groupJoin) throw new Error("move: a group join without a target");
      mod.style.left = `${d.initialLeft}px`;
      mod.style.top = `${d.initialTop}px`;
      unlift(mod);
      for (const f of d.followers) {
        f.el.style.left = `${f.startLeft}px`;
        f.el.style.top = `${f.startTop}px`;
        unlift(f.el);
      }
      withCheckpointHeld(() => joinGroupPlayers(groupJoin.target, groupJoin.joins));
      collapseWorkspaceOnTarget(); // the view stays on the target
      break;
    }
    case "merge":
    case "team-merge":
    case "switch":
    case "similarity-compare":
    case "show-match": {
      if (!overlapping) throw new Error("move: a drop action without a target");
      unlift(mod);
      const act =
        drop.kind === "merge"
          ? mergeModules
          : drop.kind === "team-merge"
            ? mergeTeamModules
            : drop.kind === "switch"
            ? switchModulePlayer
            : drop.kind === "show-match"
              ? showMatchFromModule
              : switchToSimilarityOverlay;
      // The press already checkpointed — the action adds no second step.
      withCheckpointHeld(() => act(mod, overlapping));
      collapseWorkspaceOnTarget(); // the view stays on the target, not back where the drag began
      break;
    }
    case "attach-panel": {
      // A loose map panel dropped on its map (map-panels.ts): back inside it, the module goes.
      if (!overlapping) throw new Error("move: a drop action without a target");
      unlift(mod);
      withCheckpointHeld(() => {
        const map = reattachPanel(mod);
        if (map) canvasApi().refreshModuleBody(map);
        absorb(mod);
      });
      collapseWorkspaceOnTarget();
      break;
    }
    case "show-team": {
      // The Squad or Bio goes back where it was (it stays: Ben, 2026-10-31) and the target shows its team.
      if (!overlapping) throw new Error("move: a drop action without a target");
      const team = heldTeamOf(mod);
      if (!team) throw new Error("move: a Squad or Bio drop without a team");
      mod.style.left = `${d.initialLeft}px`;
      mod.style.top = `${d.initialTop}px`;
      unlift(mod);
      // On a Stats tile or Radar showing a team, the club joins it (team-merge.ts); anywhere else, a
      // player included, it replaces what's shown (Ben, 2026-11-02).
      withCheckpointHeld(() => (teamJoinsTile(overlapping, team) ? joinTeam(overlapping, team) : applyTeamDrop(overlapping, team)));
      collapseWorkspaceOnTarget();
      break;
    }
    case "place": {
      const snapLeft = px(ind.style.left);
      const snapTop = px(ind.style.top);
      mod.style.left = `${snapLeft}px`;
      mod.style.top = `${snapTop}px`;
      unlift(mod);
      collapseWorkspace(snapLeft, snapTop, false);
      judder(mod);
      break;
    }
    case "revert": {
      mod.style.left = `${d.initialLeft}px`;
      mod.style.top = `${d.initialTop}px`;
      unlift(mod);
      collapseWorkspace(d.initialLeft, d.initialTop, true);
      break;
    }
  }

  d.groupIndicators.forEach((gi) => gi.remove());
  ind.style.display = "none";
  ind.classList.remove("invalid", "merge");

  if (wasHeaderClick) {
    toggleSelected(mod);
    // The press checkpointed for a move that never happened — take it back off, or
    // Undo would seem to do nothing once per click.
    dropLastCheckpoint();
  }
  // The halo's own pointerup ran first (this listener was added later, at the press) and
  // saw the move still on — recompute now, as main.js did once its drag had ended.
  refreshResizeDominance();
}

/** A module is being moved (the resize-handle halo pauses meanwhile). */
export function isMovingModule(): boolean {
  return drag !== null;
}

/** Start a move from a header press (desktop). */
function startMove(e: PointerEvent, mod: HTMLElement, header: HTMLElement): void {
  if (window.innerWidth <= 768) return;
  const target = e.target as Element;
  if (target.classList.contains("module-controls") || target.classList.contains("module-close") || target.closest(".module-pin")) {
    return;
  }
  // Stop the browser's focus + scroll-into-view: zoomed out, the element's layout box and
  // what you see differ, and that scroll would throw off the canvas opening below.
  e.preventDefault();

  undoApi().saveState();
  expandWorkspace();

  document.body.classList.add("is-moving-module");
  hideTooltip();
  header.setPointerCapture(e.pointerId);
  const initialLeft = leftOf(mod);
  const initialTop = topOf(mod);

  // Part of a multi-selection: the others ride along. Not part of it: that selection is
  // dropped (once the press becomes a drag) — a fresh, single-module move.
  const selected = selectedModules();
  const ind = snapIndicator();
  const indParent = ind.parentNode;
  if (!indParent) throw new Error("#snap-indicator isn't in the page");
  let followers: Follower[] = [];
  const groupIndicators: HTMLElement[] = [];
  let liftFollowers: (() => void) | null = null;
  let pendingSelectionClear: HTMLElement[] | null = null;
  if (isSelected(mod) && selected.length > 1) {
    followers = selected
      .filter((m) => m !== mod)
      .map((m) => ({
        el: m,
        ...followerOffset({ left: initialLeft, top: initialTop }, { left: leftOf(m), top: topOf(m) }),
        startLeft: leftOf(m),
        startTop: topOf(m),
      }));
    liftFollowers = () =>
      followers.forEach((f) => {
        f.el.style.zIndex = "50";
        f.el.style.opacity = "0.25";
        const gi = document.createElement("div");
        gi.className = "snap-indicator";
        gi.style.display = "block";
        gi.style.left = `${f.startLeft}px`;
        gi.style.top = `${f.startTop}px`;
        gi.style.width = f.el.style.width;
        gi.style.height = f.el.style.height;
        indParent.insertBefore(gi, ind.nextSibling);
        groupIndicators.push(gi);
      });
  } else {
    pendingSelectionClear = isSelected(mod) ? null : selected;
  }

  drag = {
    mod,
    startX: e.clientX + window.scrollX,
    startY: e.clientY + window.scrollY,
    initialLeft,
    initialTop,
    lastClientX: e.clientX,
    lastClientY: e.clientY,
    speedX: 0,
    speedY: 0,
    followers,
    groupIndicators,
    pressMoved: false,
    pendingSelectionClear,
    pendingVisuals: () => {
      liftFollowers?.();
      mod.style.zIndex = "50";
      mod.style.opacity = "0.25";
      ind.style.display = "block";
      ind.style.left = `${initialLeft}px`;
      ind.style.top = `${initialTop}px`;
      ind.classList.remove("invalid", "merge");
    },
  };
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
  if (!autoScrollRunning) {
    autoScrollRunning = true;
    requestAnimationFrame(autoScrollTick);
  }
}

/** The header's onPointerDown for one module. */
export function useModuleMove(moduleRef: RefObject<HTMLElement | null>): (e: ReactPointerEvent<HTMLElement>) => void {
  return useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      const mod = moduleRef.current;
      if (!mod) return;
      startMove(e.nativeEvent, mod, e.currentTarget);
    },
    [moduleRef],
  );
}
